import React from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AppTabs } from '../shared/tabs/AppTabs';
import { AppTabPanel } from '../shared/tabs/AppTabPanel';
import {
    CustomerDetailLeftSkeleton,
    CustomerDetailRightSkeleton,
    CustomerTabContent,
} from './components';
import { useCustomerDetailScreen } from './hooks';

export const CustomerDetailScreen = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();
    const { token } = useSelector((state) => state.auth);
    const logoInputRef = React.useRef(null);

    const {
        customer,
        isLoading,
        error,
        isUpdatingStatus,
        isUploadingLogo,
        customerLogoSrc,
        isEditingProfile,
        isSavingProfile,
        profileForm,
        activeTab,
        detailTabs,
        formatDate,
        statusClassMap,
        statusActions,
        statusOptions,
        setActiveTab,
        setLogoImageError,
        handleProfileInputChange,
        handleStartEditProfile,
        handleCancelEditProfile,
        handleSaveProfile,
        handleStatusChange,
        handleClickLogoUpload,
        handleLogoFileChange,
    } = useCustomerDetailScreen({
        token,
        id,
        navigate,
        location,
        logoInputRef,
    });

    return (
        <>
            <div className="row">
                <div className="col-xxl-4 col-xl-6">
                    <div className="card stretch stretch-full">
                        <div className="card-body">
                            {isLoading && <CustomerDetailLeftSkeleton />}

                            {!isLoading && error && (
                                <div className="alert alert-danger py-2" role="alert">
                                    {error}
                                </div>
                            )}

                            {!isLoading && !error && customer && (
                                <>
                                    <div className="mb-4 text-center">
                                        <div className="wd-150 ht-150 mx-auto mb-3 position-relative">
                                            <input
                                                ref={logoInputRef}
                                                type="file"
                                                className="d-none"
                                                accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                                                onChange={handleLogoFileChange}
                                            />
                                            <div className="avatar-image wd-150 ht-150 border border-5 border-gray-3">
                                                <img
                                                    src={customerLogoSrc}
                                                    alt="avatar"
                                                    className="img-fluid"
                                                    onError={() => setLogoImageError(true)}
                                                />
                                            </div>
                                            <div className="wd-10 ht-10 text-success rounded-circle position-absolute translate-middle" style={{ top: '76%', right: '10px' }}>
                                                <i className="bi bi-patch-check-fill"></i>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-light-brand mb-2"
                                            onClick={handleClickLogoUpload}
                                            disabled={isUploadingLogo}
                                        >
                                            {isUploadingLogo ? 'Subiendo logo...' : 'Cambiar logo'}
                                        </button>
                                        <div className="mb-4">
                                            <span className="fs-14 fw-bold d-block">{customer.name || '-'}</span>
                                            <span className="fs-12 fw-normal text-muted d-block">{customer.contactEmail || '-'}</span>
                                        </div>
                                        <div className="fs-12 fw-normal text-muted text-center d-flex flex-wrap gap-3 mb-4">
                                            <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                                <h6 className="fs-15 fw-bolder">{customer.code || '-'}</h6>
                                                <p className="fs-12 text-muted mb-0">Código</p>
                                            </div>
                                            <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                                <h6 className="fs-15 fw-bolder">
                                                    <span className={`badge ${statusClassMap[customer.status] || 'bg-soft-secondary text-secondary'}`}>
                                                        {customer.status || '-'}
                                                    </span>
                                                </h6>
                                                <p className="fs-12 text-muted mb-0">Estatus</p>
                                            </div>
                                            <div className="flex-fill py-3 px-4 rounded-1 d-none d-sm-block border border-dashed border-gray-5">
                                                <h6 className="fs-15 fw-bolder">{formatDate(customer.joinDate)}</h6>
                                                <p className="fs-12 text-muted mb-0">Alta</p>
                                            </div>
                                        </div>
                                    </div>

                                    <ul className="list-unstyled mb-4">
                                        <li className="hstack justify-content-between mb-4">
                                            <span className="text-muted fw-medium hstack gap-3"><i className="feather-user"></i>Contacto</span>
                                            <span className="float-end">{customer.contactName || '-'}</span>
                                        </li>
                                        <li className="hstack justify-content-between mb-4">
                                            <span className="text-muted fw-medium hstack gap-3"><i className="feather-phone"></i>Teléfono</span>
                                            <span className="float-end">{customer.contactPhone || '-'}</span>
                                        </li>
                                        <li className="hstack justify-content-between mb-0">
                                            <span className="text-muted fw-medium hstack gap-3"><i className="feather-mail"></i>Email</span>
                                            <span className="float-end">{customer.contactEmail || '-'}</span>
                                        </li>
                                    </ul>

                                    <div className="d-flex gap-2 text-center pt-4">
                                        {statusActions.map((action) => (
                                            <button
                                                key={action.value}
                                                type="button"
                                                className={`w-50 ${action.className}`}
                                                onClick={() => handleStatusChange(action.value)}
                                                disabled={isUpdatingStatus}
                                            >
                                                <span>{isUpdatingStatus ? 'Actualizando...' : action.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
                <div className="col-xxl-8 col-xl-6 px-xl-3 px-xxl-4">
                    <div className="card border-top-0">
                        <div className="nav nav-tabs  pt-0 pb-0">
                            <AppTabs
                                tabs={detailTabs}
                                activeTab={activeTab}
                                onChange={setActiveTab}
                                navClassName="nav nav-tabs flex-wrap w-100 customers-nav-tabs"
                                itemClassName="nav-item border-top border-end"
                            />
                        </div>

                        <div className="tab-content">
                            <AppTabPanel isActive={true} className="p-4">
                                {isLoading && <CustomerDetailRightSkeleton />}

                                {!isLoading && !error && customer && (
                                    <CustomerTabContent
                                        activeTab={activeTab}
                                        customer={customer}
                                        formatDate={formatDate}
                                        isEditingProfile={isEditingProfile}
                                        profileForm={profileForm}
                                        statusOptions={statusOptions}
                                        isSavingProfile={isSavingProfile}
                                        onProfileInputChange={handleProfileInputChange}
                                        onStartEditProfile={handleStartEditProfile}
                                        onCancelEditProfile={handleCancelEditProfile}
                                        onSaveProfile={handleSaveProfile}
                                    />
                                )}
                            </AppTabPanel>
                        </div>
                    </div>
                </div>
            </div>

            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                pauseOnHover
                draggable
                theme="light"
            />
        </>
    );
};
